import React, { useState, useMemo } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CaretUp, CaretDown, CaretUpDown, MagnifyingGlass, FunnelSimple } from '@phosphor-icons/react';

export function DataTable({
  data = [],
  columns = [],
  searchKeys = [],
  searchPlaceholder = 'Filtrer...',
  pageSize: defaultPageSize = 10,
  onRowClick,
  emptyMessage = 'Aucun enregistrement',
  showSearch = true,
  showPagination = true,
  className = '',
  mobileCardRender,
}) {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(0);
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.toLowerCase();
    return data.filter(row =>
      searchKeys.some(key => {
        const val = row[key];
        if (val == null) return false;
        return String(val).toLowerCase().includes(q);
      })
    );
  }, [data, search, searchKeys]);

  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    return [...filtered].sort((a, b) => {
      const av = a[sortKey] ?? '';
      const bv = b[sortKey] ?? '';
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      const cmp = String(av).localeCompare(String(bv), 'fr', { sensitivity: 'base' });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const paged = showPagination ? sorted.slice(page * pageSize, (page + 1) * pageSize) : sorted;

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <CaretUpDown size={12} className="text-[#8DB0A1] opacity-0 group-hover:opacity-100 transition-opacity" />;
    return sortDir === 'asc'
      ? <CaretUp size={12} className="text-[#2D5A4C] sort-active" weight="bold" />
      : <CaretDown size={12} className="text-[#2D5A4C] sort-active" weight="bold" />;
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {showSearch && (
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-xs">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5E6D66]" />
            <Input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              placeholder={searchPlaceholder}
              className="pl-8 h-8 text-xs border-[#E8E6E1]"
              data-testid="datatable-search"
            />
          </div>
          <div className="flex items-center gap-1 text-[11px] text-[#5E6D66]">
            <FunnelSimple size={12} />
            {sorted.length} / {data.length}
          </div>
        </div>
      )}

      {/* Desktop table view */}
      <div className={`rounded-lg border border-[#E8E6E1] overflow-hidden ${mobileCardRender ? 'hidden sm:block' : ''}`}>
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F9F8F5] hover:bg-[#F9F8F5]">
              {columns.map((col) => (
                <TableHead
                  key={col.key}
                  className={`text-[10px] uppercase tracking-wider font-semibold text-[#5E6D66] py-2.5 px-3 ${col.sortable !== false ? 'cursor-pointer select-none group' : ''} ${col.className || ''}`}
                  style={col.width ? { width: col.width } : undefined}
                  onClick={() => col.sortable !== false && handleSort(col.key)}
                  data-testid={`sort-${col.key}`}
                >
                  <div className="flex items-center gap-1">
                    {col.label}
                    {col.sortable !== false && <SortIcon col={col.key} />}
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {paged.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="text-center py-8 text-sm text-[#5E6D66]">
                  {search ? `Aucun resultat pour "${search}"` : emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              paged.map((row, idx) => (
                <TableRow
                  key={row.id || idx}
                  className={`transition-colors ${onRowClick ? 'cursor-pointer hover:bg-[#F9F8F5]' : ''}`}
                  onClick={() => onRowClick?.(row)}
                  data-testid={`row-${row.id || idx}`}
                >
                  {columns.map((col) => (
                    <TableCell key={col.key} className={`py-2.5 px-3 text-sm ${col.cellClassName || ''}`}>
                      {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '-')}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Mobile card view */}
      {mobileCardRender && (
        <div className="sm:hidden space-y-2" data-testid="datatable-mobile-cards">
          {paged.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-sm text-[#5E6D66]">{search ? `Aucun resultat pour "${search}"` : emptyMessage}</p>
            </div>
          ) : (
            paged.map((row, idx) => (
              <div
                key={row.id || idx}
                onClick={() => onRowClick?.(row)}
                className={onRowClick ? 'cursor-pointer' : ''}
                data-testid={`mobile-card-${row.id || idx}`}
              >
                {mobileCardRender(row, idx)}
              </div>
            ))
          )}
        </div>
      )}

      {showPagination && sorted.length > pageSize && (
        <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between pt-1" data-testid="datatable-pagination">
          <div className="flex min-w-0 items-center gap-2">
            <span className="text-[11px] text-[#5E6D66]">Lignes par page:</span>
            <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(0); }}>
              <SelectTrigger className="h-7 w-16 text-xs border-[#E8E6E1]" data-testid="datatable-page-size">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex min-w-0 flex-wrap items-center gap-1">
            <span className="basis-full sm:basis-auto shrink-0 text-[11px] text-[#5E6D66] mr-2" data-testid="datatable-pagination-summary" aria-live="polite">
              {page * pageSize + 1}-{Math.min((page + 1) * pageSize, sorted.length)} sur {sorted.length}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(0)}
              disabled={page === 0}
              className="h-7 w-7 shrink-0 p-0 text-xs"
              aria-label="Première page" title="Première page"
              data-testid="datatable-first"
            >
              &laquo;
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="h-7 w-7 shrink-0 p-0 text-xs"
              aria-label="Page précédente" title="Page précédente"
              data-testid="datatable-prev"
            >
              &lsaquo;
            </Button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) pageNum = i;
              else if (page < 3) pageNum = i;
              else if (page > totalPages - 4) pageNum = totalPages - 5 + i;
              else pageNum = page - 2 + i;
              const mobileStart = Math.min(Math.max(page - 1, 0), Math.max(totalPages - 3, 0));
              const mobileVisible = pageNum >= mobileStart && pageNum < mobileStart + 3;
              return (
                <Button
                  key={pageNum}
                  variant={pageNum === page ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPage(pageNum)}
                  className={`h-7 w-7 shrink-0 p-0 text-xs ${mobileVisible ? '' : 'hidden sm:inline-flex'} ${pageNum === page ? 'bg-[#2D5A4C] hover:bg-[#1E4136] text-white' : ''}`}
                  aria-label={`Page ${pageNum + 1}`}
                  aria-current={pageNum === page ? 'page' : undefined}
                  data-testid={`datatable-page-${pageNum}`}
                >
                  {pageNum + 1}
                </Button>
              );
            })}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="h-7 w-7 shrink-0 p-0 text-xs"
              aria-label="Page suivante" title="Page suivante"
              data-testid="datatable-next"
            >
              &rsaquo;
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(totalPages - 1)}
              disabled={page >= totalPages - 1}
              className="h-7 w-7 shrink-0 p-0 text-xs"
              aria-label="Dernière page" title="Dernière page"
              data-testid="datatable-last"
            >
              &raquo;
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
